<script setup>
// Configurações → Caixas de entrada → (caixa de WhatsApp) → aba do aparelho.
// - API oficial: "Caixa híbrida" — habilitar (conecta o celular do número como
//   aparelho), estado, e por onde saem as respostas (Neuxs::Wa::Roteador);
// - não oficial: "Aparelho" — estado, parear de novo, reconectar, desconectar.
// API: custom/app/controllers/api/v1/accounts/neuxs/whatsapp_controller.rb.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Pareamento from './Pareamento.vue';
import { createNexusApi, erroDaApi } from '../nexus/api';
import messages from '../nexus/i18n';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n({ messages });
const { accountId, accountScopedRoute } = useAccount();
const api = computed(() => createNexusApi(accountId.value));

// As classes ficam no .vue: o Tailwind não lê os .js desta pasta.
const COR = {
  conectada: 'bg-n-teal-3 text-n-teal-11',
  aguardando: 'bg-n-blue-3 text-n-blue-11',
  qr: 'bg-n-blue-3 text-n-blue-11',
  pareada: 'bg-n-blue-3 text-n-blue-11',
  desconectada: 'bg-n-amber-3 text-n-amber-11',
  qr_expirou: 'bg-n-amber-3 text-n-amber-11',
  substituida: 'bg-n-amber-3 text-n-amber-11',
  deslogada: 'bg-n-ruby-3 text-n-ruby-11',
  banida: 'bg-n-ruby-3 text-n-ruby-11',
  numero_errado: 'bg-n-ruby-3 text-n-ruby-11',
};

const carregando = ref(true);
const erro = ref('');
const caixa = ref(null);
const pareando = ref(false);
const agindo = ref('');
const roteamento = ref(null);

const hibrida = computed(() => caixa.value?.tipo === 'oficial');
const sessao = computed(() => caixa.value?.sessao);
const estado = computed(() => sessao.value?.estado);
const quando = iso =>
  iso
    ? new Date(iso).toLocaleString('pt-BR', {
        dateStyle: 'short',
        timeStyle: 'short',
      })
    : '';

const preencheRoteamento = dados => {
  const s = dados?.sessao;
  roteamento.value = s
    ? {
        roteia: s.roteia,
        ia_percentual_oficial: s.ia_percentual_oficial,
        atendente_rota: s.atendente_rota,
      }
    : null;
};

const carrega = async () => {
  carregando.value = true;
  try {
    const { data } = await api.value.whatsappCaixa(props.inbox.id);
    caixa.value = data;
    preencheRoteamento(data);
    pareando.value = Boolean(data.sessao && !data.sessao.conectada);
    erro.value = '';
  } catch (error) {
    erro.value = erroDaApi(error, t('NEUXS_NEXUS.WHATSAPP.ERRO'));
  } finally {
    carregando.value = false;
  }
};
watch(() => props.inbox.id, carrega, { immediate: true });

const age = async (nome, acao) => {
  agindo.value = nome;
  try {
    return await acao();
  } catch (error) {
    useAlert(erroDaApi(error, t('NEUXS_NEXUS.WHATSAPP.ERRO_ACAO')));
    return null;
  } finally {
    agindo.value = '';
  }
};

const atualiza = dados => {
  caixa.value = { ...caixa.value, ...dados };
};

const conecta = () => {
  if (
    hibrida.value &&
    // eslint-disable-next-line no-alert
    !window.confirm(t('NEUXS_NEXUS.WHATSAPP.CONFIRMA_HIBRIDA'))
  )
    return;
  age('conectar', async () => {
    const { data } = await api.value.conectaWhatsapp(props.inbox.id);
    atualiza(data);
    preencheRoteamento(data);
    pareando.value = true;
  });
};

const reconecta = () =>
  age('reconectar', async () => {
    await api.value.reconectaWhatsapp(props.inbox.id);
    pareando.value = true;
  });

const desconecta = () => {
  const efeito = hibrida.value
    ? t('NEUXS_NEXUS.WHATSAPP.EFEITO_HIBRIDA')
    : t('NEUXS_NEXUS.WHATSAPP.EFEITO_NAO_OFICIAL');
  if (
    // eslint-disable-next-line no-alert
    !window.confirm(t('NEUXS_NEXUS.WHATSAPP.CONFIRMA_DESCONECTAR', { efeito }))
  )
    return;
  age('desconectar', async () => {
    const { data } = await api.value.desconectaWhatsapp(props.inbox.id);
    atualiza(data);
    preencheRoteamento(data);
    pareando.value = false;
  });
};

const conectou = dados => {
  atualiza(dados);
  pareando.value = false;
};

const salvaRoteamento = () =>
  age('roteamento', async () => {
    const { data } = await api.value.roteamentoWhatsapp(props.inbox.id, {
      ...roteamento.value,
      ia_percentual_oficial: Number(roteamento.value.ia_percentual_oficial),
    });
    atualiza(data);
    preencheRoteamento(data);
    useAlert(t('NEUXS_NEXUS.WHATSAPP.SALVO'));
  });

const rotaDoUso = computed(() => accountScopedRoute('neuxs_nexus_uso'));
</script>

<template>
  <div class="flex flex-col gap-4 pb-8">
    <div v-if="carregando && !caixa" class="flex justify-center py-10">
      <Spinner />
    </div>
    <p
      v-else-if="erro"
      class="p-4 text-sm rounded-xl bg-n-ruby-2 text-n-ruby-11"
    >
      {{ erro }}
    </p>
    <template v-else-if="caixa">
      <p class="text-sm text-n-slate-11">
        {{
          hibrida
            ? t('NEUXS_NEXUS.WHATSAPP.HIBRIDA_EXPLICA')
            : t('NEUXS_NEXUS.WHATSAPP.APARELHO_EXPLICA')
        }}
      </p>

      <!-- Sem aparelho -->
      <section
        v-if="!sessao"
        class="flex flex-col items-start gap-3 p-4 border rounded-xl border-n-weak"
      >
        <span class="font-medium text-n-slate-12">
          {{
            hibrida
              ? t('NEUXS_NEXUS.WHATSAPP.HIBRIDA_DESLIGADA')
              : t('NEUXS_NEXUS.WHATSAPP.ESTADOS.sem_aparelho')
          }}
        </span>
        <Button
          :label="
            hibrida
              ? t('NEUXS_NEXUS.WHATSAPP.HABILITAR_HIBRIDA')
              : t('NEUXS_NEXUS.WHATSAPP.CONECTAR')
          "
          size="sm"
          :is-loading="agindo === 'conectar'"
          @click="conecta"
        />
      </section>

      <!-- Estado do aparelho -->
      <section
        v-else
        class="flex flex-wrap items-center justify-between gap-3 p-4 border rounded-xl border-n-weak"
      >
        <div class="flex flex-col gap-1">
          <div class="flex items-center gap-2">
            <span class="font-medium text-n-slate-12">
              {{
                hibrida
                  ? t('NEUXS_NEXUS.WHATSAPP.HIBRIDA_LIGADA')
                  : t('NEUXS_NEXUS.WHATSAPP.ABA_APARELHO')
              }}
            </span>
            <span
              class="px-2 py-0.5 text-xs rounded-md"
              :class="COR[estado] || 'bg-n-slate-3 text-n-slate-11'"
            >
              {{ t(`NEUXS_NEXUS.WHATSAPP.ESTADOS.${estado}`) }}
            </span>
          </div>
          <span class="text-sm text-n-slate-11">
            {{ caixa.telefone }}
            <template v-if="sessao.estado_em">
              ·
              {{
                t('NEUXS_NEXUS.WHATSAPP.DESDE', {
                  quando: quando(sessao.estado_em),
                })
              }}
            </template>
          </span>
          <span v-if="sessao.motivo" class="text-xs text-n-slate-10">
            {{ sessao.motivo }}
          </span>
        </div>
        <div class="flex flex-wrap gap-2">
          <Button
            v-if="estado === 'numero_errado'"
            :label="t('NEUXS_NEXUS.WHATSAPP.CONECTAR')"
            size="sm"
            :is-loading="agindo === 'conectar'"
            @click="conecta"
          />
          <template v-else>
            <Button
              v-if="!sessao.conectada && !pareando"
              :label="t('NEUXS_NEXUS.WHATSAPP.PAREAR')"
              size="sm"
              @click="pareando = true"
            />
            <Button
              :label="t('NEUXS_NEXUS.WHATSAPP.RECONECTAR')"
              size="sm"
              variant="faded"
              color="slate"
              :is-loading="agindo === 'reconectar'"
              @click="reconecta"
            />
          </template>
          <Button
            :label="
              hibrida
                ? t('NEUXS_NEXUS.WHATSAPP.DESABILITAR_HIBRIDA')
                : t('NEUXS_NEXUS.WHATSAPP.DESCONECTAR')
            "
            size="sm"
            variant="faded"
            color="ruby"
            :is-loading="agindo === 'desconectar'"
            @click="desconecta"
          />
        </div>
      </section>

      <Pareamento
        v-if="sessao && pareando && estado !== 'numero_errado'"
        :key="`${caixa.id}-${sessao.estado_em}`"
        :account-id="accountId"
        :caixa="caixa"
        @atualizou="atualiza"
        @conectado="conectou"
        @fechar="pareando = false"
      />

      <!-- Por onde saem as respostas (só na híbrida) -->
      <form
        v-if="hibrida && sessao && roteamento"
        class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak"
        @submit.prevent="salvaRoteamento"
      >
        <span class="font-medium text-n-slate-12">
          {{ t('NEUXS_NEXUS.WHATSAPP.ROTEAMENTO') }}
        </span>
        <p class="text-xs text-n-slate-11">
          {{ t('NEUXS_NEXUS.WHATSAPP.ROTEAMENTO_EXPLICA') }}
        </p>
        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <input v-model="roteamento.roteia" type="checkbox" class="!mb-0" />
          {{ t('NEUXS_NEXUS.WHATSAPP.ROTEIA') }}
        </label>
        <div class="grid gap-3 md:grid-cols-2">
          <label class="flex flex-col gap-1 text-sm text-n-slate-11">
            {{ t('NEUXS_NEXUS.WHATSAPP.IA_PERCENTUAL') }}
            <select
              v-model.number="roteamento.ia_percentual_oficial"
              class="!mb-0"
              :disabled="!roteamento.roteia"
            >
              <option v-for="n in 11" :key="n" :value="(n - 1) * 10">
                {{ (n - 1) * 10 }}%
              </option>
            </select>
            <span class="text-xs text-n-slate-10">
              {{ t('NEUXS_NEXUS.WHATSAPP.IA_DICA') }}
            </span>
          </label>
          <label class="flex flex-col gap-1 text-sm text-n-slate-11">
            {{ t('NEUXS_NEXUS.WHATSAPP.ATENDENTES') }}
            <select
              v-model="roteamento.atendente_rota"
              class="!mb-0"
              :disabled="!roteamento.roteia"
            >
              <option value="aparelho">
                {{ t('NEUXS_NEXUS.WHATSAPP.ROTAS.aparelho') }}
              </option>
              <option value="oficial">
                {{ t('NEUXS_NEXUS.WHATSAPP.ROTAS.oficial') }}
              </option>
            </select>
          </label>
        </div>
        <div>
          <Button
            type="submit"
            :label="t('NEUXS_NEXUS.WHATSAPP.SALVAR')"
            size="sm"
            :is-loading="agindo === 'roteamento'"
          />
        </div>
      </form>

      <router-link v-if="sessao" :to="rotaDoUso" class="text-sm text-n-blue-11">
        {{ t('NEUXS_NEXUS.WHATSAPP.VER_USO') }}
      </router-link>
    </template>
  </div>
</template>
